import type { Cell } from "@/logic/GridBuilder"
import type { Snackbar } from "@/utils/useSnackbar"
import type {
  DatasetDescriptor,
  SummaryVisible,
  TableLayout
} from "@kaleidoscope-solutions/engine-wasm"

import { datasetIconMap, listDiff, removeDatasetPrefix } from "@/utils"
import { ApplicationError, ErrorCode } from "@/utils/ApplicationError"
import { EngineError } from "@/utils/useEngineModel/error"
import { ModelApi } from "@/utils/useEngineModel/ModelApi"
import { useSnackbar } from "@/utils/useSnackbar"
import { CellKind, LayoutElement
} from "@kaleidoscope-solutions/engine-wasm"
import { captureException } from "@sentry/vue"

const { addSnackbar } = useSnackbar()

export type DatasetType = "Text" | "Number" | "Boolean" | "List" | "Calculation"

type snackbarClosure = (error: EngineError) => Snackbar
interface captureOptions {
  snackbar?: boolean | string | snackbarClosure
  rethrow?: boolean
}

export function captureError(error: any, options?: captureOptions) {
  if (!(error instanceof EngineError)) {
    if (options?.rethrow) {
      throw error
    }
    console.error(error)
    return
  }

  const snackbar = options?.snackbar
  if (snackbar && typeof snackbar === "boolean") {
    addSnackbar({
      type: "error",
      message: `${error.messageWithoutCode}`,
    })
  }

  if (snackbar && typeof snackbar === "string") {
    addSnackbar({
      type: "error",
      message: snackbar
    })
  }

  if (snackbar && typeof snackbar === "function") {
    addSnackbar(snackbar(error))
  }

  console.error(`${error.code}: ${error.message}`)

  captureException(error)
}

export class ModelFacade extends ModelApi {
  /**
   * View Methods
   */
  async updateTableLayout(
    viewName: string,
    contextElements: LayoutElement[],
    rowElements: LayoutElement[],
    columnElements: LayoutElement[],
    rowGroups: LayoutElement[],
    columnGroups: LayoutElement[]
  ) {
    contextElements = contextElements.map(this.cloneLayoutElement)
    rowElements = rowElements.map(this.cloneLayoutElement)
    columnElements = columnElements.map(this.cloneLayoutElement)
    rowGroups = rowGroups.map(this.cloneLayoutElement)
    columnGroups = columnGroups.map(this.cloneLayoutElement)

    try {
      return await this.api?.updateTableLayout(
        viewName,
        contextElements,
        rowElements,
        columnElements,
        rowGroups,
        // Column groups are ordered depth-first-post ordering, so the outer groups come last.
        // The engine uses semantic ordering for groups (always outer to inner). This essentially
        // means the order on screen for columns is the reverse of what the engine uses so flip the
        // visual layout order so outer groups are first in the array and inner groups are last.
        columnGroups.reverse()
      )
    } catch (error: any) {
      captureError(error, { snackbar: true })
    }
  }

  async addRowElementToTableLayout(viewName: string, elementName: string) {
    try {
      await this.api?.addRowElementToTableLayout(viewName, elementName)
    } catch (error: any) {
      captureError(error, { snackbar: true })
    }
  }

  async addColumnElementToTableLayout(viewName: string, elementName: string) {
    try {
      await this.api?.addColumnElementToTableLayout(viewName, elementName)
    } catch (error: any) {
      captureError(error, { snackbar: true })
    }
  }

  async removeColumnElementFromTableLayout(
    viewName: string,
    elementName: string
  ) {
    try {
      await this.api?.removeColumnElementFromTableLayout(viewName, elementName)
    } catch (error: any) {
      captureError(error, { snackbar: true })
    }
  }

  async removeColumnElementFromTableLayoutByCell(
    tableName: string,
    cell: Cell
  ) {
    const cellObjectId = await this.api.getCellObjectIdentifier(
      tableName,
      cell.id
    )
    return this.removeColumnElementFromTableLayout(tableName, cellObjectId)
  }

  async removeRowElementFromTableLayout(viewName: string, elementName: string) {
    try {
      await this.api?.removeRowElementFromTableLayout(viewName, elementName)
    } catch (error: any) {
      captureError(error, { snackbar: true })
    }
  }

  async setTableTotals(
    viewName: string,
    summaryVisible: SummaryVisible
  ) {
    await this.api.setSummaryVisibleOnTable(viewName, summaryVisible)
  }

  /*
   * Type Methods
   */
  async primaryListNameForType(typeName: string) {
    const list = await this.api.primaryListForType(typeName)
    return list.name
  }

  /*
   * List Methods
   */

  async renameEntityByCellId(tableName: string, cell: Cell, value: string) {
    const entityTypeName = await this.api.getCellEntityType(tableName, cell.id)
    await this.api.renameEntity(entityTypeName, cell.formattedValue, value)
  }

  async renameDataset(tableName: string, cell: Cell, value: string) {
    const cellObjectId = await this.api.getCellObjectIdentifier(
      tableName,
      cell.id
    )

    await this.api.renameDataset(cellObjectId, value)
  }

  async deleteEntityByCellId(tableName: string, cellId: string) {
    const entityTypeName = await this.api.getCellEntityType(tableName, cellId)
    const cellObjectId = await this.api.getCellObjectIdentifier(
      tableName,
      cellId
    )

    return this.api.deleteEntity(entityTypeName, cellObjectId)
  }

  async getListItemsByCellId(tableName: string, cellId: string) {
    const entityTypeName = await this.api.getCellEntityType(tableName, cellId)

    if (entityTypeName === "") {
      throw new Error("type not found")
    }

    return await this.listItemsFromType(entityTypeName)
  }

  async addNewItemToListByCellId(
    tableName: string,
    cellId: string,
    value: string
  ) {
    const entityTypeName = await this.api.getCellEntityType(tableName, cellId)

    if (entityTypeName === "") {
      throw new Error("type not found")
    }

    return await this.addNewItemToList(entityTypeName, value)
  }

  async listItemsFromType(typeName: string): Promise<string[]> {
    const listName = await this.primaryListNameForType(typeName)
    return await this.api.listItems(listName)
  }

  async addNewItemToList(typeName: string, value?: string) {
    if (!typeName) {
      throw new Error("type not found")
    }

    const listName = await this.primaryListNameForType(typeName)

    if (value === undefined || value === "") {
      await this.api.addDefaultItemToList(listName)
    } else {
      await this.api.addItemsToList(listName, [value])
    }

    const listItems = await this.api.listItems(listName)
    // Get the last item added to the list.
    return listItems[listItems.length - 1]
  }

  async getCellFullPrecisionNumber(tableName: string, cellId: string) {
    return await this.api.getCellFullPrecisionNumber(tableName, cellId)
  }

  updateItemsFromDatasetListValue(
    tableName: string,
    cellId: string,
    originalValue: string,
    cellValue: string[]
  ): Promise<void> {
    return this.fireAndForgetSave(async () => {
      const cellDatasetName = await this.api.getCellDatasetKey(
        tableName,
        cellId
      )

      const datasetName: string = cellDatasetName.name as string
      const datasetKeyTypes = cellDatasetName.keyTypes as string[]
      const datasetKeys = cellDatasetName.keys as string[]

      const before: string[] = originalValue
        ? originalValue.split(",").map((v: string) => v.trim())
        : []
      const [added, removed] = listDiff(before, cellValue)

      for (const removedValue of removed) {
        await this.api.removeItemFromDatasetListValue(
          datasetName,
          datasetKeyTypes,
          datasetKeys,
          removedValue
        )
      }

      for (const addedValue of added) {
        await this.api.setDatasetValue(
          datasetName,
          datasetKeyTypes,
          datasetKeys,
          addedValue
        )
      }
    })
  }

  filterDatasetsOnViews(
    tableLayout: TableLayout,
    dataset: DatasetDescriptor
  ): boolean {
    const inColumns =
      tableLayout.columnElements.filter(
        (ce: LayoutElement) => ce.name === dataset.name
      ) ?? []
    const inRows =
      tableLayout.rowElements.filter(
        (re: LayoutElement) => re.name === dataset.name
      ) ?? []
    return !(inColumns.length > 0 || inRows.length > 0)
  }

  // Rearrange
  async datasetsAvailableForRearrange(
    tableLayout: TableLayout,
    addExistingDataset: (dataset: DatasetDescriptor) => void
  ): Promise<any> {
    const datasets = await this.api.datasets()

    const filteredDatasets = datasets.filter((dataset) => {
      return this.filterDatasetsOnViews(tableLayout, dataset)
    })

    const types = await this.api.types()

    // Remove datasets already in the view.

    const existingDataset = types
      .map((type) => {
        return {
          label: `"${type.name}" data`,
          items: filteredDatasets
            .filter((dataset) => dataset.keyTypes.includes(type.name))
            .map((dataset) => {
              return {
                text: removeDatasetPrefix(dataset.name),
                icon: datasetIconMap(dataset.valueTypes[0] as DatasetType),
                cb: () => {
                  addExistingDataset(dataset)
                },
              }
            }),
        }
      })
      .filter((item) => item.items.length > 0)

    return existingDataset
  }

  async cellIsRelationshipName(tableName: string, cell: Cell) {
    try {
      const cellObjectId = await this.api.getCellObjectIdentifier(
        tableName,
        cell.id
      )

      if (cell.cellKind !== CellKind.DatasetName) {
        return false
      }

      const dataset = await this.api.dataset(cellObjectId)

      return dataset.relationship !== undefined
    } catch {
      return false
    }
  }

  async getImportTargets(): Promise<{ datasets: string[]; categories: string[] }> {
    const { datasets, categories } = await this.api.getImportTargets()
    return { datasets, categories }
  }

  defaultAttributes(listName: string): Promise<DatasetDescriptor[]> {
    return this.api.defaultAttributes(listName)
  }

  cloneLayoutElement(element: LayoutElement) {
    const clonedElement = new LayoutElement()
    clonedElement.name = element.name
    clonedElement.label = element.label
    clonedElement.elementKind = element.elementKind
    return clonedElement
  }

  async fireAndForgetSave<T>(logic: () => Promise<T> | T): Promise<T> {
    const logicReturn = await logic()
    void this.api.saveModel()
    return logicReturn
  }

  async save<T>(logic: () => Promise<T> | T): Promise<T> {
    const logicReturn = await logic()
    await this.api.saveModel()
    return logicReturn
  }

  async loadModel() {
    try {
      await this.api.loadModel()
    } catch (error: any) {
      captureError(error)
      throw new ApplicationError(ErrorCode.E9000)
    }
  }
}
